import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi'
import type { IconType } from 'react-icons'
import { cn } from '../../lib/utils'

export interface HorizontalScrollNavigatorProps {
  children: React.ReactNode
  /** Classe aplicada ao contêiner externo (wrapper). */
  className?: string
  /** Classe adicional aplicada à área rolável. */
  contentClassName?: string
  /** Classe extra para os botões de navegação. */
  buttonClassName?: string
  /** Fração da largura visível usada a cada clique. */
  stepRatio?: number
  /** Ícones customizados para esquerda/direita. */
  iconLeft?: IconType
  iconRight?: IconType
  /** Quando true, oculta os botões ao não haver rolagem disponível. */
  hideButtonsWhenDisabled?: boolean
  /** Remove o ajuste de padding/margem que oculta a barra de rolagem. */
  showScrollbar?: boolean
}

export interface HorizontalScrollNavigatorHandle {
  scrollToStart: () => void
  scrollToEnd: () => void
  scrollBy: (pixels: number) => void
}

const DEFAULT_STEP_RATIO = 0.6

export const HorizontalScrollNavigator = forwardRef<
  HorizontalScrollNavigatorHandle,
  HorizontalScrollNavigatorProps
>(
  (
    {
      children,
      className,
      contentClassName,
      buttonClassName,
      stepRatio = DEFAULT_STEP_RATIO,
      iconLeft: IconLeft = HiOutlineChevronLeft,
      iconRight: IconRight = HiOutlineChevronRight,
      hideButtonsWhenDisabled = true,
      showScrollbar = false,
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const sanitizedStepRatio = useMemo(() => {
      if (!Number.isFinite(stepRatio) || stepRatio <= 0) {
        return DEFAULT_STEP_RATIO
      }
      if (stepRatio > 1) {
        return 1
      }
      return stepRatio
    }, [stepRatio])

    const updateScrollState = useCallback(() => {
      const element = scrollRef.current

      if (!element) {
        setCanScrollLeft(false)
        setCanScrollRight(false)
        return
      }

      const maxScroll = element.scrollWidth - element.clientWidth
      const tolerance = 2

      if (maxScroll <= tolerance) {
        setCanScrollLeft(false)
        setCanScrollRight(false)
        return
      }

      setCanScrollLeft(element.scrollLeft > tolerance)
      setCanScrollRight(element.scrollLeft < maxScroll - tolerance)
    }, [])

    const scrollBy = useCallback(
      (direction: 'left' | 'right') => {
        const element = scrollRef.current

        if (!element) {
          return
        }

        const amount = element.clientWidth * sanitizedStepRatio

        element.scrollBy({
          left: direction === 'right' ? amount : -amount,
          behavior: 'smooth',
        })
      },
      [sanitizedStepRatio],
    )

    const scrollToExtreme = useCallback((position: 'start' | 'end') => {
      const element = scrollRef.current

      if (!element) {
        return
      }

      element.scrollTo({
        left: position === 'start' ? 0 : element.scrollWidth,
        behavior: 'smooth',
      })
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        scrollToStart: () => scrollToExtreme('start'),
        scrollToEnd: () => scrollToExtreme('end'),
        scrollBy: pixels => {
          const element = scrollRef.current
          if (!element) {
            return
          }
          element.scrollBy({ left: pixels, behavior: 'smooth' })
        },
      }),
      [scrollToExtreme],
    )

    useEffect(() => {
      const element = scrollRef.current

      if (!element) {
        return
      }

      const handleScroll = () => updateScrollState()
      
      // Atualizar estado inicial após um pequeno delay
      const initialTimer = setTimeout(updateScrollState, 100)
      
      element.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', updateScrollState)

      let resizeObserver: ResizeObserver | undefined
      let mutationObserver: MutationObserver | undefined

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          setTimeout(updateScrollState, 50)
        })
        resizeObserver.observe(element)
        Array.from(element.children).forEach(child => {
          resizeObserver?.observe(child as Element)
        })
      }

      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          if (resizeObserver) {
            Array.from(element.children).forEach(child => {
              resizeObserver.observe(child as Element)
            })
          }
          setTimeout(updateScrollState, 50)
        })

        mutationObserver.observe(element, {
          childList: true,
          subtree: true,
        })
      }

      return () => {
        clearTimeout(initialTimer)
        element.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', updateScrollState)
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
      }
    }, [updateScrollState])

    const baseButtonClasses =
      'p-2 rounded-full border transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary'

    const enabledButtonClasses =
      'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700'

    const disabledButtonClasses =
      'bg-gray-100/80 dark:bg-neutral-800/70 border-transparent opacity-40 cursor-not-allowed'

    const shouldRenderLeftButton = canScrollLeft || !hideButtonsWhenDisabled
    const shouldRenderRightButton = canScrollRight || !hideButtonsWhenDisabled

    return (
      <div className={cn('relative', className)}>
        <div
          ref={scrollRef}
          className={cn(
            'overflow-x-auto scroll-smooth',
            !showScrollbar && 'pb-4 -mb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent',
            contentClassName,
          )}
        >
          {children}
        </div>

        {shouldRenderLeftButton && (
          <button
            type='button'
            onClick={() => scrollBy('left')}
            disabled={!canScrollLeft}
            aria-label='Deslocar conteúdo para a esquerda'
            className={cn(
              'absolute left-2 top-1/2 -translate-y-1/2 z-10',
              baseButtonClasses,
              canScrollLeft ? enabledButtonClasses : disabledButtonClasses,
              buttonClassName,
            )}
          >
            <IconLeft className='h-4 w-4 text-gray-600 dark:text-gray-300' />
          </button>
        )}

        {shouldRenderRightButton && (
          <button
            type='button'
            onClick={() => scrollBy('right')}
            disabled={!canScrollRight}
            aria-label='Deslocar conteúdo para a direita'
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 z-10',
              baseButtonClasses,
              canScrollRight ? enabledButtonClasses : disabledButtonClasses,
              buttonClassName,
            )}
          >
            <IconRight className='h-4 w-4 text-gray-600 dark:text-gray-300' />
          </button>
        )}
      </div>
    )
  },
)

HorizontalScrollNavigator.displayName = 'HorizontalScrollNavigator'

export default HorizontalScrollNavigator
